import { immer } from 'zustand/middleware/immer';
import { http } from '@/lib/http';
import { measure } from '@/lib/performance';
import { createProjectScopedStore } from './projectScoped';
import { useProjectsStore } from './projects';

export type consolePane = {
  id: string;
  title: string;
  content: string;
  isRunning?: boolean;
};

export type consoleStatus = {
  isRunning: boolean;
  sessionName?: string;
  panes?: consolePane[];
};

/**
 * A minimal interface for an object that behaves like EventSource.
 * This allows for a custom implementation that supports headers.
 */
interface MinimalEventSource {
  close: () => void;
  onopen: ((this: MinimalEventSource, ev: any) => any) | null;
  onmessage: ((this: MinimalEventSource, ev: { data: string }) => any) | null;
  onerror: ((this: MinimalEventSource, ev: any) => any) | null;
}

const AUTH_TOKEN_KEY = 'mobile_auth_token';

type ConsoleState = {
  status: consoleStatus | null;
  isLoading: boolean;
  isConnected: boolean;
  error: string | null;
  // Use our custom interface to allow for a fetch-based implementation
  eventSource: MinimalEventSource | null;
};

type ConsoleActions = {
  fetchStatus: () => Promise<void>;
  startSession: () => Promise<void>;
  stopSession: () => Promise<void>;
  connect: (sessionName?: string) => void;
  disconnect: () => void;
  sendPaneInput: (paneId: string, data: string) => Promise<void>;
  resizePane: (paneId: string, rows: number, cols: number) => Promise<void>;
};

/**
 * An EventSource polyfill that uses fetch to support custom headers.
 */
class FetchEventSource implements MinimalEventSource {
  public onopen: MinimalEventSource['onopen'] = null;
  public onmessage: MinimalEventSource['onmessage'] = null;
  public onerror: MinimalEventSource['onerror'] = null;

  private abortController: AbortController;

  constructor(url: string, options: RequestInit) {
    this.abortController = new AbortController();
    this._connect(url, { ...options, signal: this.abortController.signal });
  }

  private async _connect(url: string, options: RequestInit) {
    try {
      const response = await fetch(url, options);

      if (!response.ok) {
        if (this.onerror) this.onerror(new Error(`EventSource failed: ${response.statusText}`));
        return;
      }

      if (this.onopen) this.onopen(new Event('open'));

      const reader = response.body?.getReader();
      if (!reader) {
        if (this.onerror) this.onerror(new Error('Failed to get response reader'));
        return;
      }
      
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        
        let eventEndIndex;
        while ((eventEndIndex = buffer.indexOf('\n\n')) !== -1) {
          const eventText = buffer.substring(0, eventEndIndex);
          buffer = buffer.substring(eventEndIndex + 2);

          if (eventText.startsWith(':')) continue; // Ignore comments

          const lines = eventText.split('\n').filter(line => line.startsWith('data:'));
          if (lines.length > 0) {
            const data = lines.map(line => line.substring(5).trim()).join('\n');
            if (this.onmessage) {
              this.onmessage({ data });
            }
          }
        }
      }
    } catch (e) {
      if ((e as Error).name !== 'AbortError' && this.onerror) {
        this.onerror(e);
      }
    }
  }

  public close() {
    this.abortController.abort();
  }
}

export const useConsoleStore = createProjectScopedStore<ConsoleState & ConsoleActions>('console',
  (set, get) => ({
    status: null,
    isLoading: false,
    isConnected: false,
    error: null,
    eventSource: null,
    
    fetchStatus: measure(async () => {
      set({ isLoading: true });
      try {
        const response = await http<consoleStatus>('/api/console/status');
        set({ status: response, isLoading: false, error: null });
      } catch (error) {
        console.error('Failed to fetch console status:', error);
        set({ isLoading: false, error: 'Failed to fetch status.' });
      }
    }, 'console.fetchStatus'),

    startSession: measure(async () => {
      set({ isLoading: true });
      try {
        await http('/api/console/start', { method: 'POST' });
        await get().fetchStatus();
      } catch (error) {
        console.error('Failed to start console session:', error);
        set({ isLoading: false, error: 'Failed to start session.' });
      }
    }, 'console.startSession'),

    stopSession: measure(async () => {
      set({ isLoading: true });
      try {
        await http('/api/console/stop', { method: 'POST' });
        await get().fetchStatus();
      } catch (error) {
        console.error('Failed to stop console session:', error);
        set({ isLoading: false, error: 'Failed to stop session.' });
      }
    }, 'console.stopSession'),

    connect: (sessionName) => {
      const { eventSource: existingEventSource } = get();
      if (existingEventSource) {
        console.log('Disconnecting from previous console SSE stream before connecting to new one.');
        existingEventSource.close();
        set({ isConnected: false, eventSource: null, status: null }); // Also clear status
      }

      const { activeProjectId } = useProjectsStore.getState();
      if (!activeProjectId) {
        console.error("Cannot connect to console stream without an active project.");
        set({ error: 'No active project selected.' });
        return;
      }

      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      let url = `/api/console/status-stream?project_id=${activeProjectId}`;
      if (sessionName) {
        url += `&session_name=${encodeURIComponent(sessionName)}`;
      }
      console.log('Connecting to console SSE stream:', url);
      // Use FetchEventSource instead of the native EventSource
      const es = new FetchEventSource(url, { headers });
      set({ eventSource: es as MinimalEventSource });

      es.onopen = () => {
        console.log('console SSE stream connected.');
        set({ isConnected: true, isLoading: false, error: null });
        get().fetchStatus();
      };

      es.onmessage = (event) => {
        try {
          const newStatus = JSON.parse(event.data);
          set({
            status: newStatus,
            isLoading: false,
          });
        } catch (error) {
          console.error('Error parsing console SSE message:', error);
        }
      };

      es.onerror = () => {
        console.error('console SSE stream error. Closing connection.');
        es.close();
        set({
          isConnected: false,
          eventSource: null,
          error: 'Connection to session stream lost.',
        });
      };
    },

    disconnect: () => {
      const { eventSource } = get();
      if (eventSource) {
        console.log('Disconnecting from console SSE stream.');
        eventSource.close();
        set({ isConnected: false, eventSource: null });
      }
    },

    resizePane: measure(async (paneId: string, rows: number, cols: number) => {
      try {
        await http(`/api/console/panes/${paneId}/resize`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ rows, cols }),
        });
      } catch (error) {
        console.error(`Failed to resize pane ${paneId}:`, error);
      }
    }, 'resizePane'),

    sendPaneInput: async (paneId: string, data: string) => {
      try {
        await http(`/api/console/panes/${paneId}/stdin`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data }),
        });
      } catch (error) {
        console.error(`Failed to send input to pane ${paneId}:`, error);
      }
    },
    
  })
);